import { clamp, hash01 } from '@/core/rng';
import type { SocialHabitat } from '@/sim/social/habitat';
import type { BandActivity, BandState, BandTraits, PlaceMemory } from './types';

/**
 * Band decision scoring (spec §13): every six world-hours, on the absolute
 * grid, a band weighs its needs against what it has and knows, and picks one
 * activity. Score = need relief + expected reward + remembered success +
 * trait preference − danger − distance.
 *
 * Pure functions of the band's own state and a prepared context. The noise
 * is hashed from (band id, absolute decision index, world seed) — never
 * drawn from a shared mutable stream — so the same world in the same state
 * at the same time makes the same choice, whatever else decided first
 * (spec §14).
 */

/** Decisions land every six world-hours, on the absolute grid. */
export const DECISION_STEP = 6 * 3600;

/**
 * Where need begins, on the pressure scale below: three days of food in
 * hand. Seven days is comfort and reads 0; one day is crisis and reads
 * 0.75. Anything a band does for reasons other than survival — walking to a
 * sign, going back to a holy place — is gated here rather than at some
 * private number, so "hungry" means the same thing everywhere in the game.
 */
export const WORRY = 0.35;

/**
 * How much of a band's attention need has taken: 0 while it is merely not
 * luxurious, 1 in crisis. Everything a band does for reasons other than
 * survival is *scaled* by what is left of this rather than having a
 * subtraction applied to it — so no amount of divine insistence can buy
 * through starvation. Measured: with need as a subtracted term, a god that
 * showed the same sign often enough sent a band with two days of food
 * walking away from its own supper, which is the one outcome the design
 * names as wrong. You cannot preach somebody out of hunger.
 */
export const spareAttention = (p: Pressures): number =>
  1 - clamp(Math.max(p.food - WORRY, p.water - WORRY) / 0.3, 0, 1);

/** The felt needs, each 0..1. These are what a score relieves. */
export interface Pressures {
  food: number;
  water: number;
  safety: number;
  uncertainty: number;
  /** Souls without a roof, as a fraction of the band — 0 fully housed. */
  shelter: number;
}

/**
 * Need pressure from the band's stores and surroundings (spec §11).
 * Food eases in day-breakpoints — 7 days of reserve is comfort, 3 is worry,
 * 1 is crisis. Water turns urgent much faster: the ceiling is only two days.
 * Shelter is the unhoused fraction: population against the roofs it owns.
 */
export function pressuresOf(
  band: BandState,
  here: SocialHabitat,
  danger: number,
  shelterCapacity: number,
): Pressures {
  const pop = Math.max(1, band.population);
  const days = band.reserves.food / pop;
  let food: number;
  if (days >= 7) food = 0;
  else if (days >= 3) food = ((7 - days) / 4) * 0.35;
  else if (days >= 1) food = 0.35 + ((3 - days) / 2) * 0.4;
  else food = 0.75 + (1 - days) * 0.25;
  const water = clamp(1 - band.reserves.water / pop / 1.5, 0, 1);
  // what the settlers fear is the actual hazard plus what the fuel could do
  const safety = clamp(danger + here.fireRisk * 0.35, 0, 1);
  const shelter = clamp(1 - shelterCapacity / pop, 0, 1);
  return {
    food: clamp(food, 0, 1), water, safety,
    uncertainty: clamp(band.uncertainty, 0, 1), shelter,
  };
}

/** What a remembered place seems worth; confidence discounts a stale report. */
export const placeValue = (
  p: Pick<PlaceMemory, 'foodEstimate' | 'waterEstimate' | 'dangerEstimate' | 'confidence'>,
): number =>
  (p.foodEstimate * 0.45 + p.waterEstimate * 0.3 + (1 - p.dangerEstimate) * 0.25) *
  (0.5 + 0.5 * clamp(p.confidence, 0, 1));

/**
 * Everything the scoring needs, gathered by the caller so this module stays
 * a pure function. Optional targets are pre-resolved deterministically —
 * an absent one simply removes that option from the table.
 */
export interface DecisionContext {
  seed: number;
  /** hash3 of the band's identity — the per-band salt for decision noise. */
  idHash: number;
  here: SocialHabitat;
  /** hazards.dangerAt at the band's position, 0..1. */
  danger: number;
  /** Hunting prospects, already squashed to 0..1. */
  abundance: number;
  /** Confidence of the band's memory of the cell it stands in, 0 if none. */
  familiar: number;
  /** The value of home ground, on the same scale as placeValue. */
  homeValue: number;
  /**
   * How badly wood is wanted beyond the household baseline, 0..1: 1 when an
   * open work site needs more wood than the band holds, rising as communal
   * reserves fall below ten person-days (spec §27).
   */
  woodNeed: number;
  /** A hearth burning within reach eases the felt safety pressure (spec §28). */
  comfort: number;
  /** Souls the band's own functional shelters can hold, from construction. */
  shelterCapacity: number;
  /** The band's open work site, when it has one — enables the 'build' option. */
  buildTarget?: { x: number; z: number };
  /** Richer hunting ground within reach, when home ground runs thin. */
  huntTarget?: { x: number; z: number };
  /** Best-watered spot in reach, when the current cell has none. */
  waterTarget?: { x: number; z: number };
  /** Deterministic scouting destination for this decision index. */
  exploreTarget?: { x: number; z: number };
  /**
   * The unexplained sign most worth walking to, if the band holds one, and
   * a point inside its doubt to search. Present or not, it is one row in
   * the table below — never a command. See the guard in `investigate`.
   */
  anomaly?: { salience: number; credibility: number; specificity: number };
  anomalyTarget?: { x: number; z: number };
  /**
   * The place this band would most like to stand at again, and where it is.
   * `since` is world-seconds since anyone last stood there, or Infinity if
   * nobody ever has. Going satisfies the impulse for a while — a band does
   * not walk to the shrine six times a day — so a place visited yesterday
   * barely pulls at all.
   */
  sacred?: { regard: number; visits: number; since: number };
  sacredTarget?: { x: number; z: number };
  /** Where to run when the world is burning. */
  fleeTarget?: { x: number; z: number };
}

export interface Decision {
  activity: BandActivity;
  target?: { x: number; z: number };
  /** Set when this decision is a migration: the caller re-homes there. */
  migrateTo?: PlaceMemory;
}

interface Candidate {
  a: BandActivity;
  s: number;
  target?: { x: number; z: number };
}

/**
 * One decision for one band at one absolute decision index. A max over
 * scoreTable, plus the two cases that are not scored at all: fire at the
 * door, and a remembered place worth leaving home for.
 */
export function decide(band: BandState, ctx: DecisionContext, decisionIndex: number): Decision {
  // fire at the door: nothing else is worth scoring (flee dominates, spec §13)
  if (ctx.danger > 0.5) {
    return ctx.fleeTarget
      ? { activity: 'flee', target: ctx.fleeTarget }
      : { activity: 'flee' };
  }
  const migration = migrationFor(band, ctx);
  if (migration) return migration;

  const candidates = scoreTable(band, ctx, decisionIndex);
  let best = candidates[0];
  for (const c of candidates) if (c.s > best.s) best = c;
  const out: Decision = { activity: best.a };
  if (best.target) out.target = best.target;
  return out;
}

/**
 * How much a band wants to stand at a place that matters to it (v0.3.2 §8).
 *
 * Two pulls, and the whole point is which is which. The **habit** has to be
 * earned: regard starts low and only going back raises it, so at first this
 * term is nearly nothing. The **strangeness** is what actually gets them
 * there the first few times — a standing stone near the camp is a thing to
 * go and look at again, and devotion is not available yet because nobody has
 * been given a reason for it. Novelty fades as the place becomes familiar,
 * so a place keeps a band only if returning seemed to pay; what is left when
 * the strangeness is gone is the regard those visits taught them.
 *
 * Both are muted by having just been. Going satisfies the impulse — measured
 * without that, and without sending the party home afterwards, a band spent
 * 95% of its hours standing at the stone, registered one visit ever, and
 * abandoned foraging entirely.
 *
 * And hunger still outranks all of it, because it always does.
 */
/** How long before a known sacred place is worth walking back to. Exported
 *  because `planParties` musters the trip and `venerateScore` weighs it, and
 *  the two must agree about what "a while" means. */
export const VISIT_INTERVAL = 10 * 86400;

function venerateScore(
  sacred: { regard: number; visits: number; since: number },
  t: BandTraits, p: Pressures, noise: number,
): number {
  const pull = sacred.regard * (0.55 + t.tradition * 0.85)
    + (t.curiosity * 0.75 + p.uncertainty * 0.35) / (1 + sacred.visits);
  return pull * clamp(sacred.since / VISIT_INTERVAL, 0, 1) * spareAttention(p)
    - t.caution * 0.15
    + noise;
}

/**
 * Migration: a strictly better remembered place beats staying home.
 * Tradition raises the bar to leave; wanderlust lowers it (spec §13).
 */
function migrationFor(band: BandState, ctx: DecisionContext): Decision | undefined {
  const t = band.traits;
  const p = pressuresOf(band, ctx.here, ctx.danger, ctx.shelterCapacity);
  const threshold = clamp(0.12 + t.tradition * 0.25 - t.mobility * 0.18, 0.02, 0.5);
  let bestPlace: PlaceMemory | undefined;
  let bestVal = -Infinity;
  for (const kp of band.knownPlaces) {
    if (kp.confidence < 0.3) continue;
    const d = Math.hypot(kp.x - band.homeX, kp.z - band.homeZ);
    if (d < 800) continue; // that is just home again
    const v = placeValue(kp) - d * 1e-5; // a distant paradise counts a little less
    if (v > bestVal) { bestVal = v; bestPlace = kp; }
  }
  if (bestPlace && bestVal > ctx.homeValue + threshold && clamp(p.safety - ctx.comfort, 0, 1) < 0.4) {
    return {
      activity: 'travel',
      target: { x: bestPlace.x, z: bestPlace.z },
      migrateTo: bestPlace,
    };
  }
  return undefined;
}

/**
 * The whole table, scored, before anything is picked.
 *
 * Exported because tuning this by argument has cost this project days: the
 * only way to know why a band does what it does is to read every row at the
 * moment it chose. `decide` is a max over this and nothing else, so a probe
 * that reads this reads exactly what the band read.
 */
export function scoreTable(
  band: BandState, ctx: DecisionContext, decisionIndex: number,
): ReadonlyArray<{ a: BandActivity; s: number; target?: { x: number; z: number } }> {
  const p = pressuresOf(band, ctx.here, ctx.danger, ctx.shelterCapacity);
  // a hearth at the camp's heart makes the same night feel slightly safer
  p.safety = clamp(p.safety - ctx.comfort, 0, 1);
  const t = band.traits;
  // one hash per candidate, keyed by absolute index — replay-proof by construction
  const noise = (i: number) =>
    (hash01(ctx.idHash + i * 131, decisionIndex, ctx.seed) - 0.5) * 0.18;

  const wood = band.reserves.wood / Math.max(1, band.population);
  // a full larder mutes the pull of easy food: reward terms are gated by
  // hunger, or a well-fed band gathers berries for two years straight while
  // its half-built shelter stands frameless (the soak that proved it)
  const days = band.reserves.food / Math.max(1, band.population);
  const appetite = clamp(1.15 - days / 6.5, 0.12, 1);
  /** How strongly an unexplained sign is pulling at this band, 0 when none. */
  const drawn = ctx.anomaly ? ctx.anomaly.salience * ctx.anomaly.credibility : 0;
  const candidates: Candidate[] = [
    { a: 'forage',
      s: p.food * 1.3 + ctx.here.foragePotential * 0.8 * appetite
        + ctx.familiar * 0.12 + t.tradition * 0.08 + noise(0) },
    { a: 'hunt',
      s: p.food * 1.3 + ctx.abundance * 0.75 * appetite + ctx.familiar * 0.06
        + t.mobility * 0.08 + noise(1),
      ...(ctx.huntTarget ? { target: ctx.huntTarget } : {}) },
    { a: 'seek_water', s: p.water * 2.2 + noise(2), ...(ctx.waterTarget ? { target: ctx.waterTarget } : {}) },
    { a: 'gather_wood',
      s: clamp(1 - wood / 3, 0, 1) * 0.4 + ctx.woodNeed * 0.5
        + ctx.here.woodyBiomass * 0.3 - p.food * 0.5 + noise(3) },
    // building only tempts a band whose stomach and skins are settled
    // (spec §27) — and nothing drives it like people sleeping in the open
    { a: 'build',
      s: ctx.buildTarget && p.food < 0.5 && p.water < 0.5
        ? 0.3 + p.shelter * 0.8 + t.tradition * 0.2 - ctx.woodNeed * 0.35 + noise(7)
        : -1,
      ...(ctx.buildTarget ? { target: ctx.buildTarget } : {}) },
    /**
     * A settled stomach frees the feet: satiety feeds wanderlust — but an
     * unexplained sign gives that restlessness somewhere to *be*, so the
     * pull of wandering at random weakens exactly as much as the pull of
     * going to look strengthens. Measured (tools/omen.ts): without this,
     * exploring beat investigating 0.751 to 0.595 for a curious, bold,
     * well-fed band, and signs were almost never followed by anyone. The
     * two rows were bidding for the same appetite. A sign that has been
     * walked to and found empty has no credibility left, and the feet go
     * back to wandering.
     */
    { a: 'explore',
      s: (t.curiosity * 0.55 + p.uncertainty * 0.6) * (1 - drawn * 0.6)
        + clamp(days / 14, 0, 1) * 0.3
        - t.caution * 0.3 - p.food * 0.35 + noise(4),
      ...(ctx.exploreTarget ? { target: ctx.exploreTarget } : {}) },
    /**
     * Going to look at a sign (v0.3.2 §Omen). This row is the whole safety
     * rail on the informational verb: an Omen never moves anybody, it only
     * puts one more candidate on the table, and the table is unchanged in
     * every other respect. Curiosity and how loudly the thing demanded
     * explanation pull toward it; a debunked sign pulls less because its
     * credibility is spent; caution, an empty larder and an empty waterskin
     * push it off, and the generic distance tax below does the rest. A
     * starving band does not go and look at portents.
     */
    { a: 'investigate',
      s: ctx.anomaly
        ? (ctx.anomaly.salience * ctx.anomaly.credibility * (0.45 + t.curiosity * 0.95)
           + ctx.anomaly.specificity * 0.25 + p.uncertainty * 0.3)
          // Need takes the attention rather than docking the score, so a
          // starving band cannot be shouted at until it goes. WORRY is
          // where the pressure curve itself says need begins — three days
          // of food — so "hungry" means the same thing here as everywhere.
          * spareAttention(p)
          - t.caution * 0.5 + noise(8)
        : -1,
      ...(ctx.anomalyTarget ? { target: ctx.anomalyTarget } : {}) },
    /**
     * Going back to ground that has come to matter (v0.3.2 §Anchor). An
     * Anchor grants nothing and compels nobody: this is a row like any
     * other, and it is carried by *tradition* rather than curiosity —
     * which is the difference between a sign, which is news, and a holy
     * place, which is a habit. Hunger still outranks it, because it always
     * does; a band does not walk to a shrine instead of eating.
     */
    { a: 'venerate', s: ctx.sacred ? venerateScore(ctx.sacred, t, p, noise(9)) : -1,
      ...(ctx.sacredTarget ? { target: ctx.sacredTarget } : {}) },
    { a: 'rest', s: (1 - band.cohesion) * 0.9 + 0.08 + noise(5) },
    { a: 'idle', s: 0.1 + noise(6) },
  ];

  // danger and distance tax every option that is not fleeing
  for (const c of candidates) {
    c.s -= p.safety * (0.4 + t.caution * 0.4);
    if (c.target) {
      c.s -= Math.hypot(c.target.x - band.x, c.target.z - band.z) * 1e-4 * (1 - t.mobility * 0.5);
    }
  }
  // guards by name, not index — the table grows and the guards must not care
  for (const c of candidates) {
    // seeking water with no water in reach — and already dry underfoot — is wishful
    if (c.a === 'seek_water' && ctx.here.waterAccess <= 0.45 && !ctx.waterTarget) c.s = -1;
    // exploring needs somewhere to explore toward
    if (c.a === 'explore' && !ctx.exploreTarget) c.s = -1;
    // and a sign with nowhere to search is not a sign anybody can follow
    if (c.a === 'investigate' && !ctx.anomalyTarget) c.s = -1;
    // and there is no going back to a place nobody has been to
    if (c.a === 'venerate' && !ctx.sacredTarget) c.s = -1;
  }

  return candidates;
}
